import React from 'react';
import {
  CalendarBlank, CheckCircle, EnvelopeSimple, File, Hourglass, Note, PencilSimpleLine, Phone, Receipt, Siren, Sparkle, Ticket as TicketIcon,
  UsersThree, Wrench, type Icon,
} from '@phosphor-icons/react';
import { AnteilRing, Chip, Widget, WidgetWert } from '../../../../components/immobilien/ui';
import { DL_PANEL_KOPF, type DlWidgetDef } from '../../../../components/immobilien/dienstleisterWidgets';
import { eur } from '../../../../lib/immobilien/format';
import { deDatum } from '../../../../lib/immobilien/objektDetail';
import { reaktionsStunden } from '../../../../lib/dienstleister/auftraege';
import { bauabzugNoetig, ibanKurz, istOffeneStufe, komma1, sterne, stufeLabel, ticketStufe, type TicketStufe } from '../../../../lib/dienstleister/detail';
import type { AktivitaetTyp } from '../../../../lib/kontakte/types';
import { GOLD } from '../liste/DienstleisterKachel';
import type { DlSeitenWert } from './DienstleisterSeitenKontext';

const AKT_ICON: Record<AktivitaetTyp, Icon> = {
  email: EnvelopeSimple, ticket: TicketIcon, rechnung: Receipt, dokument: File, termin: CalendarBlank, notiz: Note,
  anruf: Phone, meeting: UsersThree, aenderung: PencilSimpleLine,
};
const STUFE_ICON: Record<TicketStufe, { icon: Icon; farbe: string }> = {
  neu: { icon: Sparkle, farbe: 'var(--info)' }, beauftragt: { icon: TicketIcon, farbe: 'var(--acc)' }, in_arbeit: { icon: Wrench, farbe: 'var(--warn)' },
  erledigt: { icon: CheckCircle, farbe: 'var(--ok)' }, abgerechnet: { icon: Hourglass, farbe: 'var(--ink3)' },
};

function Zeile({ icon: IconKomp, farbe, text, rechts }: { icon: Icon; farbe: string; text: string; rechts?: string }) {
  return (
    <div className="flex items-center min-w-0" style={{ gap: 8, fontSize: 13 }}>
      <IconKomp size={15} color={farbe} style={{ flexShrink: 0 }} />
      <span className="flex-1 min-w-0 truncate" style={{ fontWeight: 600 }}>{text}</span>
      {rechts && <span className="immo-zahl" style={{ fontSize: 11, color: 'var(--ink3)' }}>{rechts}</span>}
    </div>
  );
}
const ChipZeile = ({ children }: { children: React.ReactNode }) => <span style={{ marginTop: 6 }}>{children}</span>;
const txt = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

/** Widgets der Dienstleister-Detailseite (Handoff Dienstleister §9, Prototyp W). */
export function DienstleisterWidget({ def, s, onOeffnen }: { def: DlWidgetDef; s: DlSeitenWert; onOeffnen: () => void }) {
  const kopf = DL_PANEL_KOPF[def.key];
  const basis = { titel: kopf.widget, icon: kopf.icon, onOeffnen };
  const { daten } = s;
  const z = daten.zeile;
  const d = daten.dienstleister;
  const jahr = s.heute.slice(0, 4);
  const stufen = daten.tickets.map(t => ({ t, stufe: ticketStufe(t.status) }));
  const offene = stufen.filter(x => istOffeneStufe(x.stufe));

  switch (def.key) {
    case 'profil': {
      const erledigt12 = stufen.filter(x => (x.stufe === 'erledigt' || x.stufe === 'abgerechnet') && (x.t.updated_at ?? x.t.created_at) >= `${Number(jahr) - 1}${s.heute.slice(4)}`).length;
      return (
        <Widget {...basis} akzent>
          <div className="flex flex-1 items-center flex-wrap" style={{ gap: 16 }}>
            <AnteilRing prozent={z.bewertung === null ? null : (z.bewertung / 5) * 100} groesse={110} farbe="var(--acc-ink)" spur="var(--acc-ink-fill)"
              text={z.bewertung === null ? '–' : `${komma1(z.bewertung)} ★`} />
            <div className="flex flex-col min-w-0" style={{ gap: 3 }}>
              <span className="immo-zahl" style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1 }}>
                {erledigt12} {erledigt12 === 1 ? 'Auftrag' : 'Aufträge'} erledigt · {offene.length} offen
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--acc-ink2)' }}>
                {(() => { const r = reaktionsStunden(daten.tickets, daten.historie); return r === null ? 'Ø Reaktion noch nicht messbar' : `Ø Reaktion ${komma1(r)} h`; })()}
                {' · '}{daten.bewertungen.length ? `${daten.bewertungen.length} ${daten.bewertungen.length === 1 ? 'Bewertung' : 'Bewertungen'}` : 'noch keine Bewertung'}
              </span>
            </div>
          </div>
        </Widget>
      );
    }
    case 'tickets': {
      const liste = (offene.length ? offene : stufen).slice(0, 3);
      return (
        <Widget {...basis}>
          <div className="flex flex-1 flex-col justify-center min-w-0" style={{ gap: 7 }}>
            {liste.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch keine Aufträge</span>}
            {liste.map(({ t, stufe }) => {
              const si = STUFE_ICON[stufe];
              return <Zeile key={t.id} icon={si.icon} farbe={si.farbe} text={[t.ticket_nummer, t.titel || 'Ticket'].filter(Boolean).join(' · ')} rechts={stufeLabel(stufe)} />;
            })}
          </div>
        </Widget>
      );
    }
    case 'rechnungen': {
      const bezahlt = daten.rechnungen.filter(r => (r.status ?? '').toLowerCase() === 'bezahlt').reduce((a, r) => a + (r.brutto ?? 0), 0);
      const gesamt = bezahlt + z.offen;
      return (
        <Widget {...basis}>
          <WidgetWert wert={z.offen > 0.005 ? eur(-z.offen) : daten.rechnungen.length ? 'Alle bezahlt' : 'Keine'}
            farbe={z.offen > 0.005 ? 'var(--warn)' : daten.rechnungen.length ? 'var(--ok)' : 'var(--ink3)'} sub={`Volumen ${jahr} ${eur(z.volumen, { nachkomma: 0 })}`} />
          {gesamt > 0 && (
            <div className="flex overflow-hidden" style={{ height: 6, borderRadius: 3, marginTop: 8, background: 'var(--fill2)' }} aria-hidden>
              <span style={{ width: `${(bezahlt / gesamt) * 100}%`, background: 'var(--ok)' }} />
              <span style={{ width: `${(z.offen / gesamt) * 100}%`, background: 'var(--warn)' }} />
            </div>
          )}
        </Widget>
      );
    }
    case 'objekte':
      return <Widget {...basis}><WidgetWert wert={z.objektIds.length} sub={z.objektNamen.length ? `${z.objektNamen.slice(0, 2).join(', ')}${z.objektNamen.length > 2 ? ' …' : ''}` : 'keine Zuordnung'} /></Widget>;
    case 'kategorien':
      return <Widget {...basis}><WidgetWert wert={z.kategorien.length} sub={z.kategorien.join(', ') || 'Keine Kategorie'} /></Widget>;
    case 'konditionen': {
      const stunde = d.stundensatz === null || d.stundensatz === undefined ? null : Number(d.stundensatz);
      return (
        <Widget {...basis}>
          <WidgetWert wert={stunde !== null ? `${eur(stunde, { nachkomma: 0 })} / h` : '–'}
            sub={`${z.angebot ? 'Erst Angebot' : 'Direkt beauftragen'} · bis ${eur(Number(d.freigabegrenze ?? 500), { nachkomma: 0 })}`} />
        </Widget>
      );
    }
    case 'vertraege':
      return (
        <Widget {...basis}>
          <WidgetWert wert={s.vertrag.anzahl} sub={s.vertrag.anzahl ? (s.vertrag.kostenJahr ? `${eur(s.vertrag.kostenJahr, { nachkomma: 0 })} / Jahr` : 'Kosten nicht hinterlegt') : 'nur Einzelaufträge'} />
          {s.vertrag.naechstesEnde && <ChipZeile><Chip groesse="xs" ton="info">Endet {deDatum(s.vertrag.naechstesEnde)}</Chip></ChipZeile>}
        </Widget>
      );
    case 'nachweise': {
      const vorhanden = s.nachweise.filter(n => n.vorhanden);
      const ab = vorhanden.filter(n => n.status === 'abgelaufen').length;
      const bald = vorhanden.filter(n => n.status === 'laeuft_ab').length;
      const [wert, farbe] = vorhanden.length === 0 ? ['–', 'var(--ink3)'] : ab ? [`${ab} abgelaufen`, 'var(--bad)'] : bald ? [`${bald} läuft ab`, 'var(--warn)'] : ['Alle gültig', 'var(--ok)'];
      return (
        <Widget {...basis}>
          <WidgetWert wert={wert} farbe={farbe} sub={vorhanden.length === 0 ? 'Noch keine Nachweise hinterlegt' : z.handwerk ? 'Freistellung § 48b, Haftpflicht' : 'Haftpflicht, Gewerbe'} />
          {bauabzugNoetig(z.handwerk, s.nachweise) && <ChipZeile><Chip groesse="xs" ton="bad">15 % Bauabzug</Chip></ChipZeile>}
        </Widget>
      );
    }
    case 'bewertung':
      return (
        <Widget {...basis}>
          <WidgetWert wert={z.bewertung === null ? '–' : sterne(z.bewertung)} farbe={z.bewertung === null ? 'var(--ink3)' : GOLD}
            sub={z.bewertung === null ? 'Noch keine Bewertung' : `${komma1(z.bewertung)} von 5 · ${daten.bewertungen.length} ${daten.bewertungen.length === 1 ? 'Bewertung' : 'Bewertungen'}`} />
        </Widget>
      );
    case 'regeln':
      return (
        <Widget {...basis}>
          <WidgetWert wert={s.regeln.length} sub={s.regeln.length ? s.regeln.slice(0, 2).map(r => r.name || 'Regel').join(', ') : 'Keine Regel für diese Gewerke'} />
          {s.regeln.some(r => r.ist_notfall) && <ChipZeile><Chip groesse="xs" ton="bad">Notfall</Chip></ChipZeile>}
        </Widget>
      );
    case 'bank': {
      const iban = ibanKurz(d.iban);
      return <Widget {...basis}><WidgetWert wert={iban ?? '–'} sub={txt(d.ust_id) ? `USt-ID ${txt(d.ust_id)}` : iban ? 'Keine USt-IdNr.' : 'Keine Bankverbindung'} /></Widget>;
    }
    case 'kontakt':
      return (
        <Widget {...basis}>
          <div className="flex flex-1 flex-col justify-center min-w-0" style={{ gap: 7 }}>
            {s.telefon ? <Zeile icon={Phone} farbe="var(--ok)" text={s.telefon} rechts="Büro" /> : <Zeile icon={Phone} farbe="var(--ink3)" text="Keine Telefonnummer" />}
            {s.notdienst && <Zeile icon={Siren} farbe="var(--bad)" text={s.notdienst} rechts="Notdienst 24/7" />}
            {s.email ? <Zeile icon={EnvelopeSimple} farbe="var(--info)" text={s.email} rechts="E-Mail" /> : <Zeile icon={EnvelopeSimple} farbe="var(--ink3)" text="Keine E-Mail" />}
          </div>
        </Widget>
      );
    case 'ansprech':
      return <Widget {...basis}><WidgetWert wert={daten.kontakte.length} sub={daten.kontakte.length ? daten.kontakte.slice(0, 2).map(k => k.name).join(', ') : z.ansprechpartner || 'Keine Ansprechpartner'} /></Widget>;
    case 'portal':
      return <Widget {...basis}><WidgetWert wert="Kein Portal" farbe="var(--ink3)" sub="Dienstleisterportal noch nicht angebunden" /></Widget>;
    case 'termine': {
      const kuenftig = daten.termine.filter(t => t.start_datum.slice(0, 10) >= s.heute).sort((a, b) => a.start_datum.localeCompare(b.start_datum));
      return (
        <Widget {...basis}>
          <WidgetWert wert={kuenftig[0] ? deDatum(kuenftig[0].start_datum).slice(0, 6) : '–'} sub={kuenftig[0] ? [kuenftig[0].typ, kuenftig[0].titel].filter(Boolean).join(' · ') || 'Termin' : 'Keine Termine'} />
        </Widget>
      );
    }
    case 'aktivitaet': {
      const liste = daten.aktivitaeten.slice(0, 3);
      return (
        <Widget {...basis}>
          <div className="flex flex-1 flex-col justify-center min-w-0" style={{ gap: 7 }}>
            {liste.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch keine Kommunikation</span>}
            {liste.map((a, i) => <Zeile key={`${a.typ}-${a.id ?? i}`} icon={AKT_ICON[a.typ]} farbe="var(--acc)" text={a.titel} rechts={deDatum(a.datum)} />)}
          </div>
        </Widget>
      );
    }
    case 'dokumente': {
      const dok = daten.dokumente;
      return <Widget {...basis}><WidgetWert wert={dok.length} sub={dok[0] ? `zuletzt ${dok[0].titel || dok[0].datei_name || 'Dokument'}` : 'Keine Dokumente'} /></Widget>;
    }
    case 'notizen': {
      const n = daten.notizen;
      return <Widget {...basis}><WidgetWert wert={n.length} sub={n[0] ? n[0].text : 'Keine Notizen'} /></Widget>;
    }
    case 'ki':
      return (
        <Widget {...basis}>
          <span style={{ flex: 1, marginTop: 8, fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical' }}>
            {s.ki}
          </span>
        </Widget>
      );
  }
}
